import React, { useRef } from 'react';
import { apiClient } from '../utils/apiClient';
import { yupResolver } from "@hookform/resolvers/yup";
import { defaultValues, FormValues } from "./declarations/form";
import { Box, Typography, Stack, List, ListItem } from "@mui/material";
import { useForm } from "react-hook-form";
import { useCallback, useState, useEffect } from "react";
import { PriceCheckApiData } from "./declarations/apiRequests";
import { ControlledInput } from "./ControlledInput";
import { LicenseField } from "./LicenseField";
import { OrderTotal } from "./OrderTotal";
import { License, PriceCheck } from "./declarations/license";
import { schema } from "./declarations/yup";
import { useNavigate } from 'react-router-dom';
import { CustomDialog } from "./CustomDialog";
import { ErrorDialog, RequestErrorMessage, requestErrors } from "./declarations/errors";
import TagManager from 'react-gtm-module';
import * as Constants from "./declarations/constants";
import './Paddle.css';
import MenuBar from "./MenuBar";
import Footer from "./Footer";

declare global {
  interface Window {
    Paddle: any;
  }
}

interface FormProps {
  formType: string;
}

// Define the Paddle checkout options type
interface PaddleCheckoutOptions {
  items: Array<{priceId: string | undefined, quantity: number}>;
  customer: {email: string, business: string};
  customData: {[key: string]: string};
  discountId?: string;
  discountCode?: string;
}

function Paddle({ formType }: FormProps) {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [priceCheck, setPriceCheck] = useState<PriceCheck>({
    price: formType.includes("personal") ? Constants.PERSONAL_PRICE : Constants.BUSINESS_PRICE,
    fullPrice: formType.includes("personal") ? Constants.PERSONAL_PRICE : Constants.BUSINESS_PRICE,
  } as PriceCheck);
  const [numLicensesField, setNumLicensesField] = useState(1);
  const [errorDialog, setErrorDialog] = useState<ErrorDialog>();
  const [urlDiscountCode, setUrlDiscountCode] = useState<string | null>(null);

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues, resolver: yupResolver(schema) });

  const headerText = () => {
    switch (formType) {
      case "personal":
        return "Personal License Purchase";
      case "personal-upgrade":
        return "Personal License Upgrade";
      case "business":
        return "Business License Purchase";
      case "business-upgrade":
        return "Business License Upgrade";
    }
  };

  //Send form data to the backend and have it send the license (or licenses) to the user.
  const sendLicense = useCallback(async (formData: FormValues) => {
    console.log('sendLicense()');

    const first_name = formData.firstName.trim();
    const last_name = formData.lastName.trim();
    const email_address = formData.email.trim();
    let licenseKeys = '';

    if (formType.includes('personal')) {
      try {
        const response = await apiClient.post("/purchase/personal", {
          first_name: first_name,
          last_name: last_name,
          email: email_address,
          where_heard: formData.whereHeard.trim(),
          address_for_invoice: formData.addressForInvoice.trim(),
          vat_number_for_invoice: formData.vatNumberForInvoice.trim(),
          num_licenses: 1,
          send_email_to_customer: 'false',
          send_email_to_me: 'false',
          attach_invoice: 'false',
          add_to_database: 'false',
          return_license_key: 'true',
        });

        // Store the license keys from the response
        licenseKeys = response.data;

        //Google tag manager
        TagManager.dataLayer({
          dataLayer: {
            event: 'purchase',
            description: 'personal',
            email: formData.email,
            value: priceCheck.price,
            num_licenses: 1,
            currency: 'USD',
            transaction_id: crypto.randomUUID()
          }
        });
      }
      catch (e: any) {
        setErrorDialog(requestErrors[e.message as RequestErrorMessage]);
        return;
      }
    }
    else {
      try {
        const response = await apiClient.post("/purchase/business", {
          first_name: first_name,
          last_name: last_name,
          company_name: formData.company.trim(),
          email: email_address,
          where_heard: formData.whereHeard.trim(),
          address_for_invoice: formData.addressForInvoice.trim(),
          vat_number_for_invoice: formData.vatNumberForInvoice.trim(),
          num_licenses: formData.numLicenses,
          send_email_to_customer: 'false',
          send_email_to_me: 'false',
          attach_invoice: 'false',
          add_to_database: 'false',
          return_license_key: 'true',
        });

        // Store the license keys from the response
        licenseKeys = response.data;

        //Google tag manager
        TagManager.dataLayer({
          dataLayer: {
            event: 'purchase',
            description: 'business',
            email: formData.email,
            value: priceCheck.price,
            num_licenses: formData.numLicenses,
            currency: 'USD',
            transaction_id: crypto.randomUUID()
          }
        });
      }
      catch (e: any) {
        setErrorDialog(requestErrors[e.message as RequestErrorMessage]);
        return;
      }
    }

    //Go to the success page with license keys.
    navigate('/success', { state: { licenseString: licenseKeys } });
  }, [formType, priceCheck, setErrorDialog, navigate]);

  //Paddle
  const handleCheckoutSuccess = useCallback(async (data: any) => {
    console.log("Checkout succeeded", data);
    const formValues: FormValues = {
      firstName: data.data?.custom_data?.first_name || '',
      lastName: data.data?.custom_data?.last_name || '',
      company: data.data?.custom_data?.company || '',
      email: data.data?.customer?.email || '',  // Adjust based on what’s returned
      numLicenses: data.data?.items?.at(0)?.quantity || 1, // For personal, this might be 1; for business, it can be dynamic
      whereHeard: data.data?.custom_data?.where_heard || '',
      license: '',
      addressForInvoice: '',
      vatNumberForInvoice: '',
    };

    try {
      await sendLicense(formValues);
    } catch (error) {
      console.error('Error in sendLicense:', error);
      // Check if it's an axios error with response details
      if ((error as any).response) {
        console.error('Response status:', (error as any).response.status);
        console.error('Response data:', (error as any).response.data);
      }
    }

    window.Paddle.Checkout.close();
  }, [sendLicense]);

  //Update the price when the quantity is set.
  const changePriceWithQuantity = useCallback(async (num_licenses: number) => {

    //Check price.
    console.log("changePriceWithQuantity: num_licenses = " + num_licenses);

    // If we have a URL discount code, use the discount price endpoint
    if (urlDiscountCode) {
      try {
        const response = await apiClient.post("/check_discount_price", {
          discount_code: urlDiscountCode,
          license_type: "business2",
          num_licenses: num_licenses,
          product_id: process.env.REACT_APP_PADDLE_PRODUCT_BUSINESS
        });

        if (response.data && response.data.discount_valid) {
          setPriceCheck({
            price: response.data.price,
            fullPrice: response.data.full_price
          });
          console.log(`Updated price with discount: ${response.data.full_price} -> ${response.data.price}`);
          return;
        }
      } catch (e: any) {
        console.error('Error checking discount price:', e);
        // Fall through to regular price check
      }
    }

    // Regular price check (without discount or if discount failed)
    let response;
    try {
      response = await apiClient.post("/set_num_licenses", {
        num_licenses: num_licenses,
      });
    } catch (e: any) {
      setErrorDialog(requestErrors[e.message as RequestErrorMessage]);
      return;
    }

    try {
      response = await apiClient.post("/check_price", {
        license_type: "business2",
      });
    } catch (e: any) {
      setErrorDialog(requestErrors[e.message as RequestErrorMessage]);
      return;
    }

    const data: PriceCheckApiData = response?.data;
    setPriceCheck({
      price: data.price,
      fullPrice: data.full_price
    });
    console.log(`Just set price: price=${data.price}, fullPrice=${data.full_price}`);
  }, [urlDiscountCode, setErrorDialog]);

  // Function to check price with discount code
  const checkDiscountPrice = useCallback(async (discountCode: string) => {
    console.log('=== checkDiscountPrice called ===');
    console.log('discountCode:', discountCode);
    console.log('formType:', formType);
    console.log('numLicensesField:', numLicensesField);
    
    try {
      const licenseType = formType.includes("personal") ? "personal2" : "business2";
      const numLicenses = formType === 'business' || formType === 'business-upgrade' ? numLicensesField : 1;
      
      // Get the appropriate product ID based on license type
      const productId = formType.includes("personal") 
        ? process.env.REACT_APP_PADDLE_PRODUCT_PERSONAL
        : process.env.REACT_APP_PADDLE_PRODUCT_BUSINESS;
      
      console.log('Calculated licenseType:', licenseType);
      console.log('Calculated numLicenses:', numLicenses);
      console.log('Product ID:', productId);
      
      console.log('Making API call to /check_discount_price with:', {
        discount_code: discountCode,
        license_type: licenseType,
        num_licenses: numLicenses,
        product_id: productId
      });
      
      const response = await apiClient.post("/check_discount_price", {
        discount_code: discountCode,
        license_type: licenseType,
        num_licenses: numLicenses,
        product_id: productId
      });
      
      console.log('API response:', response.data);

      if (response.data && response.data.discount_valid) {
        console.log('Discount is valid, updating price check');
        console.log('Setting priceCheck to:', {
          price: response.data.price,
          fullPrice: response.data.full_price
        });
        setPriceCheck({
          price: response.data.price,
          fullPrice: response.data.full_price
        });
        console.log(`Applied discount code ${discountCode}: ${response.data.full_price} -> ${response.data.price}`);
      } else {
        console.warn(`Discount code ${discountCode} is not valid for ${licenseType}`);
        console.log('Discount invalid, resetting to original price');
        // Reset to original price if discount is invalid
        if (formType.includes("personal")) {
          console.log('Resetting personal price to:', Constants.PERSONAL_PRICE);
          setPriceCheck({
            price: Constants.PERSONAL_PRICE,
            fullPrice: Constants.PERSONAL_PRICE,
          });
        } else {
          console.log('Calling changePriceWithQuantity for business reset');
          // For business, we need to recalculate based on number of licenses
          // Call the regular price check for business licenses
          changePriceWithQuantity(numLicenses);
        }
      }
    } catch (error) {
      console.error('Error checking discount price:', error);
    }
  }, [formType, numLicensesField, changePriceWithQuantity]);

  // Extract discount code from URL parameters and check price
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const discountCode = urlParams.get('discount');
    console.log('URL discount code found:', discountCode);
    if (discountCode) {
      setUrlDiscountCode(discountCode);
      console.log('Set URL discount code to:', discountCode);
    }
  }, []);

  // Check discount price when discount code or number of licenses changes
  useEffect(() => {
    console.log('useEffect triggered with urlDiscountCode:', urlDiscountCode);
    if (urlDiscountCode) {
      console.log('Calling checkDiscountPrice with:', urlDiscountCode);
      checkDiscountPrice(urlDiscountCode);
    }
  }, [urlDiscountCode, checkDiscountPrice]);

  //Initialize on first render.
  const paddleInitialized = useRef(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.Paddle && !paddleInitialized.current) {
      paddleInitialized.current = true;
      // Use REACT_APP_PADDLE_ENVIRONMENT if set, otherwise fall back to NODE_ENV
      const paddleEnv = process.env.REACT_APP_PADDLE_ENVIRONMENT || (process.env.NODE_ENV === 'production' ? 'production' : 'sandbox');
      console.log('Setting Paddle environment to:', paddleEnv, 'REACT_APP_PADDLE_ENVIRONMENT:', process.env.REACT_APP_PADDLE_ENVIRONMENT, 'NODE_ENV:', process.env.NODE_ENV);
      window.Paddle.Environment.set(paddleEnv);
      window.Paddle.Initialize({
        token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
        eventCallback: function(data: any) {
          switch(data.name) {
            case 'checkout.completed':
              handleCheckoutSuccess(data);
              break;
            case 'checkout.error':
              console.error('Paddle checkout error:', data);
              if (data.error && data.error.detail && data.error.detail.includes('discount')) {
                console.error('Discount-related error:', data.error.detail);
              }
              break;
            default:
              console.log('Paddle event:', data.name, data);
              break;
          }
        }
      });
    }
  }, [handleCheckoutSuccess]);

  const onSubmitButton = async (formData: FormValues) => {
    if (window.Paddle) {
      let priceId;
      const quantity = formType === 'business' || formType === 'business-upgrade' ? formData.numLicenses : 1;

      if (formType.includes('personal')) priceId = process.env.REACT_APP_PADDLE_PRICE_PERSONAL;
      else if (quantity >= 1 && quantity <= 9) priceId = process.env.REACT_APP_PADDLE_PRICE_1_9;
      else if (quantity >= 10 && quantity <= 19) priceId = process.env.REACT_APP_PADDLE_PRICE_10_19;
      else if (quantity >= 20 && quantity <= 29) priceId = process.env.REACT_APP_PADDLE_PRICE_20_29;
      else if (quantity >= 30 && quantity <= 39) priceId = process.env.REACT_APP_PADDLE_PRICE_30_39;
      else priceId = process.env.REACT_APP_PADDLE_PRICE_40_INF;

      // Capture the current licenses array from state at function call time
      console.log("Current licenses in onSubmitButton:", JSON.stringify(licenses));

      // Initialize the checkout options
      const checkoutOptions: PaddleCheckoutOptions = {
        items: [{
          priceId: priceId,
          quantity: quantity
        }],
        customer: {
          email: formData.email,
          business: formData.company,
        },
        customData: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          company_name: (formType === 'business' || formType === 'business-upgrade') ? formData.company : '',
          email: formData.email,
          where_heard: formData.whereHeard,
          address_for_invoice: formData.addressForInvoice || '',
          vat_number_for_invoice: formData.vatNumberForInvoice || '',
          old_licenses: JSON.stringify(licenses),
        }
      };

      // Handle discount codes - first check for URL discount code, then upgrade discounts
      if (urlDiscountCode) {
        console.log("Using URL discount code:", urlDiscountCode);
        console.log("Applying discount code to checkout options");
        checkoutOptions.discountCode = urlDiscountCode;
      } else if (formType.includes('-upgrade') && formData.license && formData.license.trim() !== '') {
        // For upgrade form types, get the discount ID if license key is provided
        try {
          console.log("Getting discount ID for license key:", formData.license);
          // Call the endpoint to get discount ID based on license key
          const response = await apiClient.post("/get_discount_id", {
            license_key: formData.license,
            email: formData.email,
            license_type: formType.includes("personal") ? "personal2" : "business2"
          });

          console.log("Full response from get_discount_id:", response.data);

          // Check if we received a discount ID or discount code
          if (response?.data?.discount_id) {
            console.log("Received discount ID:", response.data.discount_id);
            // Check if the discount_id looks like a code (string) or an ID (number)
            const discountValue = response.data.discount_id;
            if (typeof discountValue === 'string' && discountValue.match(/[a-zA-Z]/)) {
              // If it contains letters, it's likely a discount code
              console.log("Treating as discount code:", discountValue);
              checkoutOptions.discountCode = discountValue;
            } else {
              // Otherwise treat as a discount ID
              console.log("Treating as discount ID:", discountValue);
              checkoutOptions.discountId = discountValue;
            }
          }
        } catch (e: any) {
          console.error("Error getting discount ID:", e);
          setErrorDialog(requestErrors[e.message as RequestErrorMessage]);
          return;
        }
      }

      console.log("Opening Paddle checkout with options:", checkoutOptions);
      window.Paddle.Checkout.open(checkoutOptions);
    }
  };

  const handleLicenseChange = useCallback(
    (newLicenses: License[], priceCheck: PriceCheck) => {
      setLicenses(newLicenses);
      setPriceCheck(priceCheck);
    }, []);

  useCallback(
    (numLicenses: number) => {
      setNumLicensesField(numLicenses);
    }, []);

  const handleNumLicensesChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const numLicensesTemp = parseInt(event.target.value);
    const isNum = !Number.isNaN(numLicensesTemp);
    if (isNum && numLicensesTemp > 0) {
      setNumLicensesField(numLicensesTemp);
      changePriceWithQuantity(numLicensesTemp);
    }
  };

  // Log licenses state on render
  useEffect(() => {
    console.log("Paddle component render - current licenses state:", JSON.stringify(licenses));
  }, [licenses]);

  return (
    <div>
      <MenuBar />
      <Box sx={{
        py: 4,
        mb: 8,
        maxWidth: { xs: '95%', md: '90%', lg: '85%' },
        mx: 'auto',
        px: { xs: 2, sm: 3 }
      }}>

        {/* Header text section */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {headerText()}
          </Typography>

          <List sx={{ mb: 2, pl: 2 }} dense>
            {formType === "personal" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Want the extra features from a Premium Business license? Click <a href="./business">here</a>.
                </Typography>
              </ListItem>
            )}
            {formType === "personal-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Want the extra features from a Premium Business license? Click <a href="./business-upgrade">here</a>.
                </Typography>
              </ListItem>
            )}
            {formType === "personal" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Upgrading from a version 1 license? Click <a href="./personal-upgrade">here</a>.
                </Typography>
              </ListItem>
            )}
            {(formType === "personal" || formType === "personal-upgrade") && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Your name and email address will appear in the license key.
                </Typography>
              </ListItem>
            )}
            {formType === "business" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Upgrading from version 1? Click <a href="./business-upgrade">here</a>.
                </Typography>
              </ListItem>
            )}
            {(formType === "business" || formType === "business-upgrade") && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Your email address and either your company or your name (if you leave company blank) will appear in the license key(s).
                </Typography>
              </ListItem>
            )}
            {formType === "personal-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Enter your version 1 license key to receive a discount on your upgrade.
                </Typography>
              </ListItem>
            )}
            {formType === "personal-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  You can paste the license key, but you only need the 32 character string at the end.
                </Typography>
              </ListItem>
            )}
            {formType === "personal-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  If you think you're eligible for a discount and it says you are not, please contact us <a href="https://www.moderncsv.com/contact-us">here</a>.
                </Typography>
              </ListItem>
            )}

            {formType === "business-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Enter your version 1 license keys or version 2 Premium Personal license key to receive a discount on your upgrade.
                </Typography>
              </ListItem>
            )}
            {formType === "business-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  You can paste multiple license keys.
                </Typography>
              </ListItem>
            )}
            {formType === "business-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  You can paste entire license keys, but you only need the 32 character string at the end.
                </Typography>
              </ListItem>
            )}
            {formType === "business-upgrade" && (
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  If you think you're eligible for a discount and it says you are not, please contact us <a href="https://www.moderncsv.com/contact-us">here</a>.
                </Typography>
              </ListItem>
            )}
{/*            {(
              <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                <Typography variant="body2">
                  Your license key email will be sent to the email address entered in this step.
                </Typography>
              </ListItem>
            )}*/}
          </List>
        </Box>

        <form onSubmit={handleSubmit(onSubmitButton)}>
          <Box>

            <Stack spacing={1.5}>
              {(formType === "personal-upgrade" || formType === "business-upgrade") && (
                <LicenseField
                  name="license"
                  control={control}
                  label={"License key" + ((formType === 'business-upgrade') ? "s" : "") + " (optional)"}
                  rows={4}
                  onLicenseChange={handleLicenseChange}
                  formType={formType}
                  multiline
                />
              )}

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <ControlledInput
                    name="firstName"
                    control={control}
                    label="First Name"
                    error={errors.firstName}
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <ControlledInput
                    name="lastName"
                    control={control}
                    label="Last Name"
                    error={errors.lastName}
                    fullWidth
                    required
                  />
                </Box>
              </Box>

              {(formType === "business" || formType === "business-upgrade") && (
                <ControlledInput
                  name="company"
                  control={control}
                  label="Company (optional)"
                  error={errors.company}
                />
              )}

              <ControlledInput
                name="email"
                control={control}
                label="Email"
                error={errors.email}
                required
              />

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  {(formType === "business" || formType === "business-upgrade") &&
                    //https://stackoverflow.com/a/64933799
                    <ControlledInput
                      name="numLicenses"
                      control={control}
                      type="number"
                      label="Number of licenses"
                      error={errors.numLicenses}
                      onChange={handleNumLicensesChange}
                      inputProps={{ min: "1", step: "1", pattern: "([^0-9]*)" }}
                      value={numLicensesField}
                      required
                    />
                  }
                </Box>
              </Box>

              {(!formType.includes("upgrade")) && (
                <ControlledInput
                  name="whereHeard"
                  control={control}
                  label="Where did you hear about us? (optional)"
                  error={errors.whereHeard}
                />
              )}

              <OrderTotal
                licenses={licenses}
                priceCheck={priceCheck}
                formType={formType}
                isEstimate={true}
              />

              <button type="submit" className="submit-button">
                Continue to Payment
              </button>

            </Stack>
          </Box>
        </form>
      </Box>
      <Footer />

      <CustomDialog
        opened={!!errorDialog}
        title={errorDialog?.title || ''}
        body={errorDialog?.message || ''}
      />
    </div>
  );
}

export default Paddle;
